import { createFileRoute, Navigate } from '@tanstack/react-router';
import { useEffect, useMemo, useState } from 'react';
import { LabsPublishedList, LabsReviewProvider, LabsReviewQueue, type LabsReviewModule } from '@ludolio/shared-labs';
import {
  Badge,
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Progress,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Switch,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  toast,
} from '@ludolio/shared-ui';
import { getApiErrorMessage } from '@/lib/api-error';
import { labsReviewApi, labsSettingsApi, labsTeamAdminApi, type LabAdminTeam } from '@/services/api';
import type { LabsSettings } from '@/types';

type Visibility = LabsSettings['defaultVisibility'];

const VISIBILITY_OPTIONS: Array<{ value: Visibility; label: string }> = [
  { value: 'organization', label: 'University' },
  { value: 'unit', label: 'Unit' },
  { value: 'private', label: 'Project team' },
];

function formatGiB(bytes: number): string {
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
}

export const Route = createFileRoute('/_authenticated/labs')({
  component: LabsPage,
});

function LabsPage() {
  const [settings, setSettings] = useState<LabsSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [movingOwner, setMovingOwner] = useState(false);
  const [teamsVersion, setTeamsVersion] = useState(0);

  const reviewModule = useMemo<LabsReviewModule>(() => ({
    api: labsReviewApi,
    getErrorMessage: (err) => getApiErrorMessage(err, 'Something went wrong. Please try again.'),
  }), []);

  useEffect(() => {
    labsSettingsApi
      .get()
      .then(setSettings)
      .catch((err) => setError(getApiErrorMessage(err, 'Failed to load Labs settings')))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="p-6 text-sm text-muted-foreground">Loading Labs…</div>;
  if (settings && !settings.enabled) return <Navigate to="/dashboard" replace />;

  return (
    <div className="mx-auto max-w-5xl space-y-6 p-6">
      <div className="flex items-center justify-between gap-3">
        <h1 className="text-2xl font-bold">Labs</h1>
        {settings && (
          <Button variant="outline" onClick={() => setMovingOwner(true)}>Move owner</Button>
        )}
      </div>
      {error && <p className="text-sm text-destructive" role="alert">{error}</p>}
      {settings && <OwnerlessTeamsAlert key={teamsVersion} />}
      <Dialog open={movingOwner} onOpenChange={setMovingOwner}>
        <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>Move owner</DialogTitle>
          </DialogHeader>
          <MoveOwner onMoved={() => setTeamsVersion((current) => current + 1)} />
        </DialogContent>
      </Dialog>

      {settings && (
        <LabsReviewProvider module={reviewModule}>
          <Tabs defaultValue="reviews">
            <TabsList>
              <TabsTrigger value="reviews">Review</TabsTrigger>
              <TabsTrigger value="published">Published</TabsTrigger>
              <TabsTrigger value="settings">Settings</TabsTrigger>
            </TabsList>
            <TabsContent value="reviews" className="mt-6">
              <LabsReviewQueue scope="organization" emptyMessage="Nothing is waiting for review" />
            </TabsContent>
            <TabsContent value="published" className="mt-6">
              <LabsPublishedList scope="organization" emptyMessage="Nothing is published yet" />
            </TabsContent>
            <TabsContent value="settings" className="mt-6">
              <LabsSettingsForm settings={settings} onSaved={setSettings} />
            </TabsContent>
          </Tabs>
        </LabsReviewProvider>
      )}
    </div>
  );
}

function LabsSettingsForm({ settings, onSaved }: { settings: LabsSettings; onSaved: (settings: LabsSettings) => void }) {
  const [defaultVisibility, setDefaultVisibility] = useState<Visibility>(settings.defaultVisibility);
  const [remixingEnabled, setRemixingEnabled] = useState(settings.remixingEnabled);
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [saving, setSaving] = useState(false);

  const termsAccepted = Boolean(settings.internalRemixTerms.acceptedAt);
  const mustAcceptTerms = remixingEnabled && !termsAccepted;
  const dirty = defaultVisibility !== settings.defaultVisibility
    || remixingEnabled !== settings.remixingEnabled
    || (mustAcceptTerms && acceptTerms);
  const usagePercent = settings.storageQuotaBytes > 0
    ? Math.round((settings.storageUsedBytes / settings.storageQuotaBytes) * 100)
    : 0;

  const save = async () => {
    setSaving(true);
    try {
      const next = await labsSettingsApi.update({
        defaultVisibility,
        remixingEnabled,
        ...(mustAcceptTerms ? { acceptInternalRemixTerms: true } : {}),
      });
      setAcceptTerms(false);
      onSaved(next);
      toast.success('Labs settings saved');
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Failed to save Labs settings'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <form
      className="space-y-6 rounded-lg border border-border bg-card p-5"
      onSubmit={(event) => {
        event.preventDefault();
        void save();
      }}
    >
      <div className="grid gap-2 sm:grid-cols-[12rem_minmax(0,1fr)] sm:items-center">
        <span className="text-sm font-medium">Labs address</span>
        {settings.labsUrl ? (
          <a href={settings.labsUrl} target="_blank" rel="noopener noreferrer" className="truncate text-sm underline">
            {settings.labsUrl}
          </a>
        ) : (
          <span className="text-sm text-muted-foreground">Not set yet. Contact Ludolio to set your Labs address.</span>
        )}
      </div>

      <div className="grid gap-2 sm:grid-cols-[12rem_minmax(0,1fr)] sm:items-center">
        <Label htmlFor="labs-default-visibility">Who can play by default</Label>
        <Select value={defaultVisibility} onValueChange={(value) => setDefaultVisibility(value as Visibility)} disabled={saving}>
          <SelectTrigger id="labs-default-visibility" className="sm:w-64">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {VISIBILITY_OPTIONS.map((option) => (
              <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="grid gap-2 sm:grid-cols-[12rem_minmax(0,1fr)] sm:items-center">
        <Label htmlFor="labs-remixing">Allow remixing</Label>
        <Switch
          id="labs-remixing"
          checked={remixingEnabled}
          onCheckedChange={setRemixingEnabled}
          disabled={saving}
        />
      </div>

      {mustAcceptTerms && (
        <div className="grid gap-3 sm:grid-cols-[12rem_minmax(0,1fr)]">
          <span className="text-sm font-medium">Internal remix terms</span>
          <div className="space-y-3 sm:max-w-xl">
            <p className="whitespace-pre-line text-sm text-muted-foreground">
              Version {settings.internalRemixTerms.version}. {settings.internalRemixTerms.text}
            </p>
            <div className="flex items-start gap-3">
              <Checkbox id="labs-accept-terms" checked={acceptTerms} onCheckedChange={(value) => setAcceptTerms(value === true)} disabled={saving} />
              <Label htmlFor="labs-accept-terms" className="pt-1 font-normal leading-5">
                Accept internal remix terms, version {settings.internalRemixTerms.version}
              </Label>
            </div>
          </div>
        </div>
      )}

      <div className="grid gap-2 sm:grid-cols-[12rem_minmax(0,1fr)] sm:items-center">
        <span className="text-sm font-medium">Storage</span>
        <div className="space-y-2 sm:w-80">
          <Progress value={usagePercent} aria-label="Labs storage used" />
          <p className="text-xs text-muted-foreground">
            {formatGiB(settings.storageUsedBytes)} of {formatGiB(settings.storageQuotaBytes)} used
          </p>
        </div>
      </div>

      <div className="flex justify-end">
        <Button type="submit" disabled={!dirty || saving || (mustAcceptTerms && !acceptTerms)}>
          {saving ? 'Saving…' : 'Save settings'}
        </Button>
      </div>
    </form>
  );
}

const ROLE_LABELS = { owner: 'Owner', manager: 'Manager', member: 'Member' } as const;

function OwnerlessTeamsAlert() {
  const [total, setTotal] = useState(0);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    labsTeamAdminApi
      .listOrphaned(1)
      .then((result) => setTotal(result.total))
      .catch(() => setTotal(0));
  }, []);

  if (total === 0 && !open) return null;

  return (
    <>
      {total > 0 && (
        <div
          role="status"
          className="flex flex-col gap-3 rounded-lg border border-amber-200 bg-amber-50 p-4 text-amber-900 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-200 sm:flex-row sm:items-center sm:justify-between"
        >
          <p className="text-sm font-medium">
            {total === 1 ? '1 team has no owner' : `${total} teams have no owner`}
          </p>
          <Button size="sm" onClick={() => setOpen(true)}>
            {total === 1 ? 'Assign owner' : 'Assign owners'}
          </Button>
        </div>
      )}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>Teams without an owner</DialogTitle>
          </DialogHeader>
          <OrphanedTeams onTotalChange={setTotal} />
        </DialogContent>
      </Dialog>
    </>
  );
}

function OrphanedTeams({ onTotalChange }: { onTotalChange?: (total: number) => void }) {
  const [page, setPage] = useState(1);
  const [teams, setTeams] = useState<LabAdminTeam[] | null>(null);
  const [total, setTotal] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [error, setError] = useState('');

  const load = (nextPage: number) => {
    labsTeamAdminApi
      .listOrphaned(nextPage)
      .then((result) => {
        setTeams(result.teams);
        setTotal(result.total);
        setPageSize(result.pageSize);
        setError('');
        onTotalChange?.(result.total);
      })
      .catch((err) => setError(getApiErrorMessage(err, 'Failed to load teams')));
  };

  useEffect(() => {
    load(page);
  }, [page]);

  const assign = async (teamId: string, email: string) => {
    try {
      await labsTeamAdminApi.assignOwner(teamId, email);
      toast.success('Owner assigned');
      load(page);
      return true;
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Failed to assign an owner'));
      return false;
    }
  };

  const pageCount = Math.max(1, Math.ceil(total / pageSize));

  if (!teams && !error) return <p className="text-sm text-muted-foreground">Loading teams…</p>;

  return (
    <div className="space-y-4">
      {error && <p className="text-sm text-destructive" role="alert">{error}</p>}
      {teams?.length === 0 && <p className="text-sm text-muted-foreground">Every team has an owner.</p>}
      {teams && teams.length > 0 && (
        <ul className="divide-y rounded-lg border">
          {teams.map((team) => (
            <AdminTeamCard
              key={team.id}
              team={team}
              actionLabel="Assign owner"
              busyLabel="Assigning…"
              placeholder="name@university.edu"
              onSubmit={(email) => assign(team.id, email)}
            />
          ))}
        </ul>
      )}
      {pageCount > 1 && (
        <div className="flex justify-end gap-2">
          <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage((current) => current - 1)}>Previous</Button>
          <Button variant="outline" size="sm" disabled={page >= pageCount} onClick={() => setPage((current) => current + 1)}>Next</Button>
        </div>
      )}
    </div>
  );
}

function MoveOwner({ onMoved }: { onMoved: () => void }) {
  const [email, setEmail] = useState('');
  const [result, setResult] = useState<{ email: string; ownerUserId: string | null; teams: LabAdminTeam[] } | null>(null);
  const [finding, setFinding] = useState(false);

  const find = async (address: string) => {
    setFinding(true);
    try {
      const next = await labsTeamAdminApi.listOwned(address);
      setResult({ email: address, ...next });
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Failed to find teams'));
    } finally {
      setFinding(false);
    }
  };

  const move = async (teamId: string, newOwnerEmail: string) => {
    if (!result?.ownerUserId) return false;
    try {
      await labsTeamAdminApi.moveOwner(teamId, result.ownerUserId, newOwnerEmail);
      toast.success('Owner moved');
      onMoved();
      await find(result.email);
      return true;
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Failed to move the owner'));
      return false;
    }
  };

  return (
    <div className="space-y-4">
      <form
        className="flex flex-col gap-3 sm:flex-row sm:items-end"
        onSubmit={(event) => {
          event.preventDefault();
          const address = email.trim().toLowerCase();
          if (address) void find(address);
        }}
      >
        <div className="min-w-0 flex-1 space-y-2">
          <Label htmlFor="move-owner-email">Current owner</Label>
          <Input
            id="move-owner-email"
            type="email"
            placeholder="name@university.edu"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            disabled={finding}
          />
        </div>
        <Button type="submit" disabled={finding || !email.trim()} className="sm:w-36">
          {finding ? 'Finding…' : 'Find teams'}
        </Button>
      </form>
      {result && result.teams.length === 0 && (
        <p className="text-sm text-muted-foreground">{result.email} doesn't own any teams.</p>
      )}
      {result && result.teams.length > 0 && (
        <ul className="divide-y rounded-lg border">
          {result.teams.map((team) => (
            <AdminTeamCard
              key={team.id}
              team={team}
              actionLabel="Move owner"
              busyLabel="Moving…"
              placeholder="New owner's email"
              onSubmit={(newOwnerEmail) => move(team.id, newOwnerEmail)}
            />
          ))}
        </ul>
      )}
    </div>
  );
}

function AdminTeamCard({
  team,
  actionLabel,
  busyLabel,
  placeholder,
  onSubmit,
}: {
  team: LabAdminTeam;
  actionLabel: string;
  busyLabel: string;
  placeholder: string;
  onSubmit: (email: string) => Promise<boolean>;
}) {
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const inputId = `owner-${team.id}`;

  const submit = async () => {
    const address = email.trim();
    if (!address) return;
    setBusy(true);
    const done = await onSubmit(address);
    setBusy(false);
    if (done) setEmail('');
  };

  return (
    <li className="space-y-4 p-4">
      <div className="min-w-0">
        <h3 className="truncate font-medium">{team.name}</h3>
        <p className="truncate text-sm text-muted-foreground">
          {team.projectTotal === 0
            ? 'No projects'
            : team.projects.map((project) => project.title).join(', ')}
          {team.projectTotal > team.projects.length ? ` | ${team.projectTotal} projects` : ''}
        </p>
      </div>
      <ul className="space-y-2">
        {team.members.map((member) => (
          <li key={member.userId} className="grid grid-cols-[minmax(0,1fr)_auto_auto] items-center gap-3">
            <div className="min-w-0">
              <p className="truncate text-sm font-medium">{member.name}</p>
              <p className="truncate text-sm text-muted-foreground">{member.email}</p>
            </div>
            <Badge variant="secondary">{ROLE_LABELS[member.role]}</Badge>
            <div className="w-20 text-right">
              {!member.inOrganization && <Badge variant="outline">Outside</Badge>}
            </div>
          </li>
        ))}
      </ul>
      <form
        className="flex flex-col gap-3 sm:flex-row"
        onSubmit={(event) => {
          event.preventDefault();
          void submit();
        }}
      >
        <Label htmlFor={inputId} className="sr-only">New owner email</Label>
        <Input
          id={inputId}
          type="email"
          placeholder={placeholder}
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          disabled={busy}
        />
        <Button type="submit" disabled={busy || !email.trim()} className="sm:w-36">
          {busy ? busyLabel : actionLabel}
        </Button>
      </form>
    </li>
  );
}
